import { Button, Card, Chip, faDigits, Field, formatDateShort, formatMoney, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { accountingApi, api, type AccountingVoucher, type AccountingVoucherLineInput } from "../../api.js";
import { ErrorLine, useAction } from "../common.js";

interface TreeNode {
  id: string;
  name: string;
}

interface DraftLineState {
  id: string;
  accountId: string;
  debitRial: number;
  creditRial: number;
  description: string;
  detailId: string;
}

export function VouchersTab() {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const [showEditor, setShowEditor] = useState(false);
  const [editingVoucher, setEditingVoucher] = useState<AccountingVoucher | null>(null);

  // Draft Editor form state
  const [docDate, setDocDate] = useState(() => {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Tehran",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  });
  const [docDescription, setDocDescription] = useState("");
  const [lines, setLines] = useState<DraftLineState[]>([
    { id: "1", accountId: "", debitRial: 0, creditRial: 0, description: "", detailId: "" },
    { id: "2", accountId: "", debitRial: 0, creditRial: 0, description: "", detailId: "" },
  ]);

  // Reverse Voucher state
  const [reversingVoucher, setReversingVoucher] = useState<AccountingVoucher | null>(null);
  const [reverseDate, setReverseDate] = useState(() => {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Tehran",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  });
  const [reverseReason, setReverseReason] = useState("");

  // Inspect Voucher state
  const [inspectingVoucher, setInspectingVoucher] = useState<AccountingVoucher | null>(null);

  // Queries
  const vouchersQuery = useQuery({
    queryKey: ["accounting", "vouchers"],
    queryFn: () => accountingApi.listVouchers(),
  });

  const accountsQuery = useQuery({
    queryKey: ["accounting", "accounts"],
    queryFn: () => accountingApi.listAccounts(),
  });

  const orgsQuery = useQuery({
    queryKey: ["organizations"],
    queryFn: () => api<TreeNode[]>("/organizations"),
  });

  const vouchers = vouchersQuery.data ?? [];
  const accounts = accountsQuery.data ?? [];
  const orgs = orgsQuery.data ?? [];

  // Filter accounts for voucher lines (only leaf / subsidiary / detail accounts)
  const leafAccounts = useMemo(() => {
    return accounts.filter(
      (a) => a.status === "active" && (a.level === "subsidiary" || a.level === "detail"),
    );
  }, [accounts]);

  // Account map for fast lookup
  const accountMap = useMemo(() => {
    return new Map(accounts.map((a) => [a.id, a]));
  }, [accounts]);

  // Filtered vouchers list
  const filteredVouchers = useMemo(() => {
    return vouchers.filter((v) => {
      if (statusFilter !== "all" && v.status !== statusFilter) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const vNumStr = v.voucherNumber ? String(v.voucherNumber) : "";
        return v.description.toLowerCase().includes(q) || v.date.includes(q) || vNumStr.includes(q);
      }
      return true;
    });
  }, [vouchers, statusFilter, search]);

  // Calculated totals for draft lines
  const totalDebit = useMemo(() => {
    return lines.reduce((sum, l) => sum + (Number.isFinite(l.debitRial) ? l.debitRial : 0), 0);
  }, [lines]);

  const totalCredit = useMemo(() => {
    return lines.reduce((sum, l) => sum + (Number.isFinite(l.creditRial) ? l.creditRial : 0), 0);
  }, [lines]);

  const isBalanced = totalDebit === totalCredit && totalDebit > 0;
  const difference = Math.abs(totalDebit - totalCredit);

  // Open editor for new draft
  const openNewDraft = () => {
    setEditingVoucher(null);
    setDocDate(
      new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Tehran",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date()),
    );
    setDocDescription("");
    setLines([
      {
        id: "1",
        accountId: leafAccounts[0]?.id ?? "",
        debitRial: 0,
        creditRial: 0,
        description: "",
        detailId: "",
      },
      {
        id: "2",
        accountId: leafAccounts[1]?.id ?? "",
        debitRial: 0,
        creditRial: 0,
        description: "",
        detailId: "",
      },
    ]);
    setShowEditor(true);
  };

  // Open editor for existing draft
  const openEditDraft = (v: AccountingVoucher) => {
    setEditingVoucher(v);
    setDocDate(v.date);
    setDocDescription(v.description);
    setLines(
      v.lines.map((l, idx) => ({
        id: String(idx + 1),
        accountId: l.accountId,
        debitRial: l.debitRial,
        creditRial: l.creditRial,
        description: l.description ?? "",
        detailId: l.detailId ?? "",
      })),
    );
    setShowEditor(true);
  };

  // Save Draft action
  const saveDraftAction = useAction(
    async (postNow: boolean) => {
      // Build lines input
      const linesInput: AccountingVoucherLineInput[] = lines.map((l) => ({
        accountId: l.accountId,
        debitRial: l.debitRial,
        creditRial: l.creditRial,
        description: l.description.trim() || undefined,
        detailId: l.detailId || null,
        detailType: l.detailId ? "organization" : null,
      }));

      if (editingVoucher && editingVoucher.version !== undefined) {
        // Update existing draft
        await accountingApi.updateVoucher(editingVoucher.id, editingVoucher.version, {
          date: docDate,
          description: docDescription.trim(),
          lines: linesInput,
        });
        if (postNow) {
          // Fetch updated voucher to get latest version, then post
          const updated = await accountingApi.getVoucher(editingVoucher.id);
          if (updated.version !== undefined) {
            await accountingApi.postVoucher(updated.id, updated.version);
          }
        }
      } else {
        // Create new voucher
        const created = await accountingApi.createVoucher({
          date: docDate,
          description: docDescription.trim(),
          status: postNow ? "posted" : "draft",
          lines: linesInput,
        });
        if (postNow) {
          const v = await accountingApi.getVoucher(created.id);
          if (v.status !== "posted" && v.version !== undefined) {
            await accountingApi.postVoucher(v.id, v.version);
          }
        }
      }

      setShowEditor(false);
    },
    [
      ["accounting", "vouchers"],
      ["accounting", "reports"],
    ],
  );

  // Post Voucher action
  const postAction = useAction(
    async (v: AccountingVoucher) => {
      if (v.version === undefined) return;
      await accountingApi.postVoucher(v.id, v.version);
    },
    [
      ["accounting", "vouchers"],
      ["accounting", "reports"],
    ],
  );

  // Delete Draft action
  const deleteDraftAction = useAction(
    async (v: AccountingVoucher) => {
      if (v.version === undefined) return;
      const ok = window.confirm("آیا از حذف این پیش‌نویس سند اطمینان دارید؟");
      if (!ok) return;
      await accountingApi.deleteVoucher(v.id, v.version);
    },
    [["accounting", "vouchers"]],
  );

  // Reverse Voucher action
  const reverseAction = useAction(async () => {
    if (!reversingVoucher) return;
    await accountingApi.reverseVoucher(reversingVoucher.id, {
      date: reverseDate,
      reason: reverseReason.trim(),
    });
    setReversingVoucher(null);
    setReverseReason("");
  }, [
    ["accounting", "vouchers"],
    ["accounting", "reports"],
  ]);

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Top Toolbar */}
      <div className="row wrap justify-between items-center" style={{ gap: "var(--s3)" }}>
        <div className="row wrap items-center" style={{ gap: "var(--s3)" }}>
          <input
            type="text"
            className="input"
            placeholder="جستجوی شماره سند، شرح یا تاریخ..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            style={{ minWidth: "260px" }}
          />

          <select
            className="select"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
            }}
            style={{ width: "auto" }}
          >
            <option value="all">همه اسناد</option>
            <option value="posted">اسناد قطعی شده</option>
            <option value="draft">پیش‌نویس‌ها</option>
          </select>
        </div>

        <Button onClick={openNewDraft}>+ ثبت سند حسابداری جدید</Button>
      </div>

      <ErrorLine error={postAction.error || deleteDraftAction.error} />

      {/* Vouchers Table */}
      {vouchersQuery.isLoading ? (
        <Card>
          <div className="center p-8 text-secondary">در حال بارگذاری اسناد...</div>
        </Card>
      ) : filteredVouchers.length === 0 ? (
        <Card>
          <div className="center p-8 text-secondary">سندی برای نمایش یافت نشد.</div>
        </Card>
      ) : (
        <div className="table-wrap">
          <table className="table" dir="rtl">
            <thead>
              <tr>
                <th style={{ width: "110px" }}>شماره سند</th>
                <th style={{ width: "120px" }}>تاریخ سند</th>
                <th>شرح کلی سند</th>
                <th style={{ width: "140px", textAlign: "left" }}>مبلغ سند</th>
                <th style={{ width: "110px" }}>وضعیت</th>
                <th style={{ width: "200px", textAlign: "left" }}>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {filteredVouchers.map((v) => {
                const docSumDebit = v.lines.reduce((s, l) => s + l.debitRial, 0);

                return (
                  <tr key={v.id}>
                    <td className="bold" style={{ fontFamily: "monospace", fontSize: "1.05rem" }}>
                      {v.voucherNumber ? faDigits(v.voucherNumber) : "پیش‌نویس"}
                    </td>
                    <td>{formatDateShort(v.date)}</td>
                    <td>
                      <div>
                        <span className="bold">{v.description}</span>
                        {v.isReversal && (
                          <span style={{ marginRight: "8px" }}>
                            <Chip tone="coral">سند برگشتی</Chip>
                          </span>
                        )}
                        <div className="text-secondary text-sm">{faDigits(v.lines.length)} ردیف سند</div>
                      </div>
                    </td>
                    <td className="bold" style={{ textAlign: "left" }}>
                      {formatMoney(docSumDebit)}
                    </td>
                    <td>
                      {v.status === "posted" ? (
                        <Chip tone="ok">ثبت قطعی</Chip>
                      ) : (
                        <Chip tone="blue">پیش‌نویس</Chip>
                      )}
                    </td>
                    <td style={{ textAlign: "left" }}>
                      <div className="row justify-end items-center" style={{ gap: "var(--s2)" }}>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setInspectingVoucher(v);
                          }}
                        >
                          مشاهده
                        </Button>

                        {v.status === "draft" && (
                          <>
                            <Button
                              variant="ghost"
                              onClick={() => {
                                openEditDraft(v);
                              }}
                            >
                              ویرایش
                            </Button>
                            <Button
                              variant="primary"
                              onClick={() => {
                                postAction.run(v);
                              }}
                              disabled={postAction.busy}
                            >
                              ثبت قطعی
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => {
                                deleteDraftAction.run(v);
                              }}
                              disabled={deleteDraftAction.busy}
                            >
                              حذف
                            </Button>
                          </>
                        )}

                        {v.status === "posted" && !v.isReversal && (
                          <Button
                            variant="ghost"
                            onClick={() => {
                              setReversingVoucher(v);
                              setReverseReason(
                                `برگشت سند شماره ${v.voucherNumber ? String(v.voucherNumber) : ""}`,
                              );
                            }}
                          >
                            سند برگشتی
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal: Draft Editor */}
      {showEditor && (
        <div className="modal-backdrop">
          <div className="modal-box" style={{ maxWidth: "860px", width: "100%" }}>
            <Panel
              title={
                editingVoucher
                  ? `ویرایش سند شماره ${editingVoucher.voucherNumber ? String(editingVoucher.voucherNumber) : "پیش‌نویس"}`
                  : "ثبت سند حسابداری جدید"
              }
            >
              <div className="stack" style={{ gap: "var(--s4)" }}>
                <ErrorLine error={saveDraftAction.error} />

                <div className="row wrap" style={{ gap: "var(--s3)" }}>
                  <div style={{ flex: 1, minWidth: "160px" }}>
                    <Field
                      id="voucher-date"
                      label="تاریخ سند"
                      type="text"
                      placeholder="YYYY-MM-DD"
                      value={docDate}
                      onChange={(e) => {
                        setDocDate(e.target.value);
                      }}
                    />
                  </div>
                  <div style={{ flex: 2, minWidth: "300px" }}>
                    <Field
                      id="voucher-desc"
                      label="شرح کلی سند"
                      type="text"
                      placeholder="مثال: خرید مواد اولیه / دریافت مطالبات ماهانه"
                      value={docDescription}
                      onChange={(e) => {
                        setDocDescription(e.target.value);
                      }}
                    />
                  </div>
                </div>

                {/* Lines Table */}
                <div className="stack" style={{ gap: "var(--s2)" }}>
                  <div className="row justify-between items-center">
                    <span className="bold">ردیف‌های آرتیکل سند حسابداری:</span>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setLines([
                          ...lines,
                          {
                            id: String(Date.now()),
                            accountId: leafAccounts[0]?.id ?? "",
                            debitRial: 0,
                            creditRial: 0,
                            description: "",
                            detailId: "",
                          },
                        ]);
                      }}
                    >
                      + افزودن ردیف
                    </Button>
                  </div>

                  <div className="table-wrap">
                    <table className="table" dir="rtl">
                      <thead>
                        <tr>
                          <th style={{ width: "220px" }}>حساب معین</th>
                          <th style={{ width: "160px" }}>تفصیلی شناور</th>
                          <th>شرح ردیف</th>
                          <th style={{ width: "140px" }}>بدهکار (ریال)</th>
                          <th style={{ width: "140px" }}>بستانکار (ریال)</th>
                          <th style={{ width: "40px" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {lines.map((l, index) => (
                          <tr key={l.id}>
                            <td>
                              <select
                                className="select"
                                value={l.accountId}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setLines(
                                    lines.map((item, i) =>
                                      i === index ? { ...item, accountId: val } : item,
                                    ),
                                  );
                                }}
                              >
                                <option value="">-- انتخاب حساب --</option>
                                {leafAccounts.map((a) => (
                                  <option key={a.id} value={a.id}>
                                    {a.code} — {a.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select
                                className="select"
                                value={l.detailId}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setLines(
                                    lines.map((item, i) => (i === index ? { ...item, detailId: val } : item)),
                                  );
                                }}
                              >
                                <option value="">—</option>
                                {orgs.map((o) => (
                                  <option key={o.id} value={o.id}>
                                    {o.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input
                                type="text"
                                className="input"
                                placeholder="شرح ردیف..."
                                value={l.description}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setLines(
                                    lines.map((item, i) =>
                                      i === index ? { ...item, description: val } : item,
                                    ),
                                  );
                                }}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="input"
                                value={l.debitRial || ""}
                                placeholder="۰"
                                onChange={(e) => {
                                  const val = parseInt(e.target.value, 10) || 0;
                                  setLines(
                                    lines.map((item, i) =>
                                      i === index
                                        ? {
                                            ...item,
                                            debitRial: val,
                                            creditRial: val > 0 ? 0 : item.creditRial,
                                          }
                                        : item,
                                    ),
                                  );
                                }}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="input"
                                value={l.creditRial || ""}
                                placeholder="۰"
                                onChange={(e) => {
                                  const val = parseInt(e.target.value, 10) || 0;
                                  setLines(
                                    lines.map((item, i) =>
                                      i === index
                                        ? {
                                            ...item,
                                            creditRial: val,
                                            debitRial: val > 0 ? 0 : item.debitRial,
                                          }
                                        : item,
                                    ),
                                  );
                                }}
                              />
                            </td>
                            <td>
                              {lines.length > 2 && (
                                <button
                                  type="button"
                                  className="btn btn-ghost"
                                  style={{ padding: "4px 8px", color: "var(--color-danger, #d32f2f)" }}
                                  onClick={() => {
                                    setLines(lines.filter((_, i) => i !== index));
                                  }}
                                >
                                  ✕
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: "var(--panel-bg, #f9f9f9)", fontWeight: "bold" }}>
                          <td colSpan={3}>مجموع آرتیکل‌های سند:</td>
                          <td>{formatMoney(totalDebit)}</td>
                          <td>{formatMoney(totalCredit)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* Balanced-Total Indicator (نشانگر توازن سند) */}
                  <div
                    className="p-4 rounded-lg row justify-between items-center"
                    style={{
                      background: isBalanced ? "#e8f5e9" : "#fff3e0",
                      border: `1px solid ${isBalanced ? "#a5d6a7" : "#ffcc80"}`,
                      color: isBalanced ? "#1b5e20" : "#e65100",
                    }}
                  >
                    <div className="row items-center" style={{ gap: "var(--s2)" }}>
                      <span style={{ fontSize: "1.2rem" }}>{isBalanced ? "✅" : "⚠️"}</span>
                      <span className="bold">
                        {isBalanced
                          ? `تراز سند متوازن است (مجموع: ${formatMoney(totalDebit)})`
                          : `سند نامتوازن است! اختلاف بدهکار و بستانکار: ${formatMoney(difference)}`}
                      </span>
                    </div>

                    <div className="text-sm">
                      بدهکار: {formatMoney(totalDebit)} | بستانکار: {formatMoney(totalCredit)}
                    </div>
                  </div>
                </div>

                {/* Footer Buttons */}
                <div className="row justify-end" style={{ gap: "var(--s3)", marginTop: "var(--s4)" }}>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowEditor(false);
                    }}
                  >
                    انصراف
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      saveDraftAction.run(false);
                    }}
                    disabled={saveDraftAction.busy || !docDescription.trim()}
                  >
                    ذخیره به عنوان پیش‌نویس
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => {
                      saveDraftAction.run(true);
                    }}
                    disabled={saveDraftAction.busy || !isBalanced || !docDescription.trim()}
                  >
                    {saveDraftAction.busy ? "در حال ثبت..." : "ثبت قطعی سند"}
                  </Button>
                </div>
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* Modal: Reverse Voucher */}
      {reversingVoucher && (
        <div className="modal-backdrop">
          <div className="modal-box" style={{ maxWidth: "480px", width: "100%" }}>
            <Panel
              title={`صدور سند برگشتی برای سند شماره ${reversingVoucher.voucherNumber ? String(reversingVoucher.voucherNumber) : ""}`}
            >
              <div className="stack" style={{ gap: "var(--s4)" }}>
                <ErrorLine error={reverseAction.error} />

                <Field
                  id="rev-date"
                  label="تاریخ سند برگشتی"
                  type="text"
                  value={reverseDate}
                  onChange={(e) => {
                    setReverseDate(e.target.value);
                  }}
                />

                <Field
                  id="rev-reason"
                  label="علت برگشت سند"
                  type="text"
                  value={reverseReason}
                  onChange={(e) => {
                    setReverseReason(e.target.value);
                  }}
                />

                <div className="row justify-end" style={{ gap: "var(--s3)", marginTop: "var(--s4)" }}>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setReversingVoucher(null);
                    }}
                  >
                    انصراف
                  </Button>
                  <Button
                    onClick={() => {
                      reverseAction.run(undefined);
                    }}
                    disabled={reverseAction.busy || !reverseReason.trim()}
                  >
                    {reverseAction.busy ? "در حال ثبت..." : "تأیید و صدور سند معکوس"}
                  </Button>
                </div>
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* Modal: Inspect Voucher Lines */}
      {inspectingVoucher && (
        <div className="modal-backdrop">
          <div className="modal-box" style={{ maxWidth: "720px", width: "100%" }}>
            <Panel
              title={`جزئیات سند شماره ${inspectingVoucher.voucherNumber ? String(inspectingVoucher.voucherNumber) : "پیش‌نویس"}`}
            >
              <div className="stack" style={{ gap: "var(--s4)" }}>
                <div className="row justify-between text-sm">
                  <span>تاریخ: {formatDateShort(inspectingVoucher.date)}</span>
                  <span>وضعیت: {inspectingVoucher.status === "posted" ? "ثبت قطعی" : "پیش‌نویس"}</span>
                </div>
                <div className="bold">{inspectingVoucher.description}</div>

                <div className="table-wrap">
                  <table className="table" dir="rtl">
                    <thead>
                      <tr>
                        <th>ردیف</th>
                        <th>حساب</th>
                        <th>شرح آرتیکل</th>
                        <th>بدهکار</th>
                        <th>بستانکار</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inspectingVoucher.lines.map((l, i) => {
                        const acc = accountMap.get(l.accountId);
                        return (
                          <tr key={l.id}>
                            <td>{faDigits(i + 1)}</td>
                            <td>{acc ? `${acc.code} - ${acc.name}` : l.accountId}</td>
                            <td>{l.description || "—"}</td>
                            <td>{formatMoney(l.debitRial)}</td>
                            <td>{formatMoney(l.creditRial)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="row justify-end">
                  <Button
                    onClick={() => {
                      setInspectingVoucher(null);
                    }}
                  >
                    بستن
                  </Button>
                </div>
              </div>
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
}
